import { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Container,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    VStack,
    Spinner,
    Alert,
    AlertIcon,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tab,
} from "@chakra-ui/react";

interface Order {
    id: string;
    market: string;
    price: string;
    quantity: string;
    timestamp: string;
    side: string;
    status: string;
}

interface Balance {
    currency: string;
    balance: string;
    lockedBalance: string;
}

function App() {
    const [orderHistory, setOrderHistory] = useState<Order[]>([]);
    const [activeOrders, setActiveOrders] = useState<Order[]>([]);
    const [balances, setBalances] = useState<Balance[]>([]);
    const [loading, setLoading] = useState({
        history: true,
        active: true,
        balance: true,
    });
    const [error, setError] = useState({
        history: "",
        active: "",
        balance: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [historyRes, activeRes, balanceRes] = await Promise.all([
                    axios.get(
                        "https://order-backend.dev.paraacrypto.com/api/coindcx/orders/history",
                    ),
                    axios.get(
                        "https://order-backend.dev.paraacrypto.com/api/coindcx/orders/active",
                    ),
                    axios.get(
                        "https://order-backend.dev.paraacrypto.com/api/coindcx/balance",
                    ),
                ]);

                // Transform and validate the response data
                setOrderHistory(
                    Array.isArray(historyRes.data) ? historyRes.data : [],
                );
                setActiveOrders(
                    Array.isArray(activeRes.data) ? activeRes.data : [],
                );
                setBalances(
                    Array.isArray(balanceRes.data) ? balanceRes.data : [],
                );
            } catch (err) {
                if (err instanceof Error) {
                    setError({
                        history: err.message,
                        active: err.message,
                        balance: err.message,
                    });
                }
            } finally {
                setLoading({
                    history: false,
                    active: false,
                    balance: false,
                });
            }
        };

        fetchData();
    }, []);

    const renderOrderTable = (orders: Order[], type: string) => (
        <Box overflowX="auto">
            {loading[type as keyof typeof loading] ? (
                <Box textAlign="center" py={10}>
                    <Spinner />
                </Box>
            ) : error[type as keyof typeof error] ? (
                <Alert status="error">
                    <AlertIcon />
                    {error[type as keyof typeof error]}
                </Alert>
            ) : !orders || orders.length === 0 ? (
                <Alert status="info">
                    <AlertIcon />
                    No orders found
                </Alert>
            ) : type === "history" ? (
                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>ID</Th>
                            <Th>orderId</Th>
                            <Th>symbol</Th>
                            <Th>Price</Th>
                            <Th>Quantity</Th>
                            <Th>fee</Th>
                            <Th>Side</Th>
                            <Th>Status</Th>
                            <Th>Time</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {orders.map((order: any) => (
                            <Tr key={order?.id}>
                                <Td>{order?.id}</Td>
                                <Td>{order?.order_id}</Td>
                                <Td>
                                    {order?.symbol
                                        ? order?.symbol
                                        : order?.market}
                                </Td>
                                <Td>{order?.price}</Td>
                                <Td>{order?.quantity}</Td>
                                <Td>{order?.fee_amount}</Td>
                                <Td
                                    color={
                                        order?.side === "buy"
                                            ? "green.500"
                                            : "red.500"
                                    }
                                >
                                    {order?.side.toUpperCase()}
                                </Td>
                                <Td>{order?.status}</Td>
                                <Td>
                                    {new Date(
                                        parseInt(order?.timestamp),
                                    ).toLocaleString()}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            ) : (
                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>ID</Th>
                            <Th>orderId</Th>
                            <Th>symbol</Th>
                            <Th>order Type</Th>
                            <Th>total quantity</Th>
                            <Th>remaining quantity</Th>
                            <Th>avg price</Th>
                            <Th>price per unit</Th>
                            <Th>fee</Th>
                            <Th>Side</Th>
                            <Th>Status</Th>
                            <Th>createdAt</Th>
                            <Th>updatedAt</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {orders.map((order: any) => (
                            <Tr key={order?.id}>
                                <Td>{order?.id}</Td>
                                <Td>{order?.client_order_id}</Td>
                                <Td>{order?.market}</Td>
                                <Td>{order?.order_type}</Td>
                                <Td>{order?.total_quantity}</Td>
                                <Td>{order?.remaining_quantity}</Td>
                                <Td>{order?.avg_price}</Td>
                                <Td>{order?.price_per_unit}</Td>
                                <Td>{order?.fee_amount}</Td>
                                <Td
                                    color={
                                        order?.side === "buy"
                                            ? "green.500"
                                            : "red.500"
                                    }
                                >
                                    {order?.side.toUpperCase()}
                                </Td>
                                <Td>{order?.status}</Td>
                                <Td>
                                    {new Date(
                                        parseInt(order?.created_at),
                                    ).toLocaleString()}
                                </Td>
                                <Td>
                                    {new Date(
                                        parseInt(order?.updated_at),
                                    ).toLocaleString()}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            )}
        </Box>
    );

    const renderBalanceTable = () => (
        <Box overflowX="auto">
            {loading.balance ? (
                <Box textAlign="center" py={10}>
                    <Spinner />
                </Box>
            ) : error.balance ? (
                <Alert status="error">
                    <AlertIcon />
                    {error.balance}
                </Alert>
            ) : (
                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>Currency</Th>
                            <Th>Available Balance</Th>
                            <Th>Locked Balance</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {balances.map((balance) => (
                            <Tr key={balance.currency}>
                                <Td>{balance.currency}</Td>
                                <Td>{balance.balance}</Td>
                                <Td>{balance.lockedBalance}</Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            )}
        </Box>
    );

    return (
        <Container maxW="container.xl" py={8}>
            <VStack spacing={8} align="stretch">
                <Heading as="h1" size="xl" textAlign="center">
                    CoinDCX Trading Dashboard
                </Heading>

                <Tabs isFitted variant="enclosed">
                    <TabList>
                        <Tab>Order History</Tab>
                        <Tab>Active Orders</Tab>
                        <Tab>Balances</Tab>
                    </TabList>

                    <TabPanels>
                        <TabPanel>
                            <Heading as="h2" size="lg" mb={4}>
                                Order History
                            </Heading>
                            {renderOrderTable(orderHistory, "history")}
                        </TabPanel>

                        <TabPanel>
                            <Heading as="h2" size="lg" mb={4}>
                                Active Orders
                            </Heading>
                            {renderOrderTable(activeOrders, "active")}
                        </TabPanel>

                        <TabPanel>
                            <Heading as="h2" size="lg" mb={4}>
                                Wallet Balances
                            </Heading>
                            {renderBalanceTable()}
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </VStack>
        </Container>
    );
}

export default App;
